import React, { useContext, useEffect, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';

// Components
import Typography from '@components/UI-KIT/Typography';
import DateFormat from '@components/utils/DateFormat';
import Button from '@components/UI-KIT/Button';
import Footer from '@components/UI-KIT/Footer';
import AmountFormat from '@components/utils/AmountFormat';
import Receipt from '@components/Receipt';
import HorizontalTable from '@components/UI-KIT/HorizontalTable';

// Context
import { SideDrawerContext } from '@components/UI-KIT/SideDrawer';

// Material-ui
import {
    Grid,
    TableContainer,
    Table,
    TableBody,
    TableRow,
    TableCell
} from '@material-ui/core';

// Entities
import { Transaction } from '@/src/services/account-transaction';
import {
    Income,
    Outgoing,
    Cash,
    Bank,
    POL,
    Download,
    Delete
} from '@/assets/icons';

// Styles
import './transactionDetails.scss';
import VARIABLES from '@/assets/variables';

interface Props {
    transaction: Transaction;
    handleDeleteTransaction: () => void;
}

const TransactionDetails: React.FC<Props> = ({ transaction, handleDeleteTransaction }) => {
    const printableTicket = useRef();
    const download = useReactToPrint({
        content: () => printableTicket.current,
        documentTitle: 'Detalle de transacción'
    });

    const sideDrawerContext = useContext(SideDrawerContext);

    useEffect(() => {
        sideDrawerContext.setPreventClosing(false);
    }, [ ]);

    const accountTypeIcon = () => {
        switch (transaction.account.type) {
            case 'Cash':
                return <Cash />;
            case 'Bank':
                return <Bank />;
            case 'POL':
                return <POL />;
        }
    };

    const imputation = () => {
        switch (transaction.imputation) {
            case 'Tenant':
                return 'Inquilino';
            case 'Administrator':
                return 'Administración';
            case 'Propietor':
                return 'Propietario';
            default:
                return '-';
        }
    };

    return (
        <>
            <TableContainer>
                <Table
                    className="AOL-transactions-detail-table"
                >
                    <TableBody>
                        <TableRow>
                            <TableCell>
                                <Typography
                                    color="primary-dark"
                                    variant="table-semibold"
                                >
                                    Tipo de transacción
                                </Typography>
                            </TableCell>
                            <TableCell
                                align="right"
                            >
                                <Grid
                                    container
                                    alignItems="center"
                                    justify="flex-end"
                                    wrap="nowrap"
                                >
                                    {transaction.amount >= 0 ? (
                                        <>
                                            <Typography
                                                color="primary-dark"
                                            >
                                                Ingreso
                                            </Typography>
                                            <Income />
                                        </>
                                    ) : (
                                        <>
                                            <Typography
                                                color="primary-dark"
                                            >
                                                Egreso
                                            </Typography>
                                            <Outgoing />
                                        </>
                                    )}
                                </Grid>
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                <Typography
                                    color="primary-dark"
                                    variant="table-semibold"
                                >
                                    Monto
                                </Typography>
                            </TableCell>
                            <TableCell
                                align="right"
                            >
                                <Typography
                                    color="primary-dark"
                                >
                                    <AmountFormat
                                        value={transaction.amount}
                                    />
                                </Typography>
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                <Typography
                                    color="primary-dark"
                                    variant="table-semibold"
                                >
                                    Cuenta
                                </Typography>
                            </TableCell>
                            <TableCell
                                align="right"
                            >
                                <Grid
                                    container
                                    alignItems="center"
                                    justify="flex-end"
                                >
                                    <Typography
                                        color="primary-dark"
                                    >
                                        {transaction.account.name}
                                    </Typography>
                                    {accountTypeIcon()}
                                </Grid>
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                <Typography
                                    color="primary-dark"
                                    variant="table-semibold"
                                >
                                    Concepto
                                </Typography>
                            </TableCell>
                            <TableCell
                                align="right"
                            >
                                <Typography
                                    color="primary-dark"
                                >
                                    {transaction.concept.name}
                                </Typography>
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                <Typography
                                    color="primary-dark"
                                    variant="table-semibold"
                                >
                                    Asociado a
                                </Typography>
                            </TableCell>
                            <TableCell
                                align="right"
                            >
                                <Typography
                                    color="primary-dark"
                                >
                                    {imputation()}
                                </Typography>
                            </TableCell>
                        </TableRow>
                        {transaction.imputation !== 'Administrator' && (
                            <TableRow>
                                <TableCell>
                                    <Typography
                                        color="primary-dark"
                                        variant="table-semibold"
                                    >
                                        {`Nombre del ${imputation().toLowerCase()}`}
                                    </Typography>
                                </TableCell>
                                <TableCell
                                    align="right"
                                >
                                    <Typography
                                        color="primary-dark"
                                    >
                                        { transaction.associatedTenantName
                                            ? transaction.associatedTenantName
                                            : transaction.associatedPropietorName
                                        }
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        { transaction.imputation === 'Tenant' && (
                            <TableRow>
                                <TableCell>
                                    <Typography
                                        color="primary-dark"
                                        variant="table-semibold"
                                    >
                                        Incluir en cobro de alquiler
                                    </Typography>
                                </TableCell>
                                <TableCell
                                    align="right"
                                >
                                    <Typography
                                        color="primary-dark"
                                    >
                                        {transaction.includedInBilling
                                            ? 'Sí'
                                            : 'No'
                                        }
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        <TableRow>
                            <TableCell>
                                <Typography
                                    color="primary-dark"
                                    variant="table-semibold"
                                >
                                    Fecha
                                </Typography>
                            </TableCell>
                            <TableCell
                                align="right"
                            >
                                <Typography
                                    color="primary-dark"
                                >
                                    <DateFormat
                                        value={transaction.date}
                                    />
                                </Typography>
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                <Typography
                                    color="primary-dark"
                                    variant="table-semibold"
                                >
                                    Comprobante
                                </Typography>
                            </TableCell>
                            <TableCell
                                align="right"
                            >
                                {transaction.attachProofIds.length === 0 ? (
                                    <Typography
                                        color="primary-dark"
                                    >
                                        -
                                    </Typography>
                                ) : (
                                    <Grid
                                        container
                                        alignItems="center"
                                        justify="flex-end"
                                    >
                                        <Typography
                                            color="primary-dark"
                                        >
                                            {transaction.attachProofIds[0].originalname}
                                        </Typography>
                                        <a
                                            href={transaction.attachProofIds[0].url}
                                            title="Descargar comprobante"
                                            download={transaction.attachProofIds[0].originalname}
                                        >
                                            <Download
                                                color={VARIABLES.palette.secondary_color}
                                            />
                                        </a>
                                    </Grid>
                                )}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                <Typography
                                    color="primary-dark"
                                    variant="table-semibold"
                                >
                                    Descripción
                                </Typography>
                            </TableCell>
                            <TableCell
                                align="right"
                            >
                                <Typography
                                    color="primary-dark"
                                >
                                    {transaction.description}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
            <Footer
                className="AOL-transactions-detail-footer"
            >
                <Button
                    label="Descargar detalle"
                    level={2}
                    fullWidth
                    onClick={download}
                    icon={(
                        <Download
                            color={VARIABLES.palette.secondary_color}
                        />
                    )}
                />
                { transaction.isDeletable && (
                    <Button
                        label="Eliminar transacción"
                        level={1}
                        onClick={handleDeleteTransaction}
                        fullWidth
                        errorColor
                        icon={(
                            <Delete
                                color={VARIABLES.palette.background_color}
                            />
                        )}
                    />
                )}
            </Footer>
            <Grid
                style={{ display: 'none' }}
            >
                <Grid
                    ref={printableTicket}
                >
                    <Receipt
                        title="Detalles de transacción"
                        heroTitle="Detalles de transacción"
                        sections={[
                            <HorizontalTable
                                title=""
                                data={[
                                    {
                                        label: 'Tipo de transacción',
                                        value: (
                                            <Grid
                                                container
                                                alignItems="center"
                                                justify="flex-end"
                                                wrap="nowrap"
                                                spacing={1}
                                            >
                                                {transaction.amount >= 0 ? (
                                                    <>
                                                        <Grid
                                                            item
                                                        >
                                                            <Typography
                                                                color="primary-dark"
                                                            >
                                                                Ingreso
                                                            </Typography>
                                                        </Grid>
                                                        <Grid
                                                            item
                                                        >
                                                            <Income />
                                                        </Grid>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Grid
                                                            item
                                                        >
                                                            <Typography
                                                                color="primary-dark"
                                                            >
                                                                Egreso
                                                            </Typography>
                                                        </Grid>
                                                        <Grid
                                                            item
                                                        >
                                                            <Outgoing />
                                                        </Grid>
                                                    </>
                                                )}
                                            </Grid>
                                        )
                                    },
                                    {
                                        label: 'Monto',
                                        value: (
                                            <AmountFormat
                                                value={transaction.amount}
                                            />
                                        )
                                    },
                                    { label: 'Cuenta', value: (
                                        <Grid
                                            container
                                            alignItems="center"
                                            justify="flex-end"
                                            wrap="nowrap"
                                            spacing={1}
                                        >
                                            <Grid
                                                item
                                            >
                                                {transaction.account.name}
                                            </Grid>
                                            <Grid
                                                item
                                            >
                                                {accountTypeIcon()}
                                            </Grid>
                                        </Grid>

                                    ) },
                                    { label: 'Concepto', value: transaction.concept.name },
                                    { label: 'Asociado a', value: imputation() },
                                    {
                                        label: `Nombre del ${imputation().toLowerCase()}`,
                                        value: transaction.associatedTenantName
                                            ? transaction.associatedTenantName
                                            : transaction.associatedPropietorName,
                                        hide: transaction.imputation === 'Administrator'
                                    },
                                    { label: 'Incluir en cobro de alquiler', value: '' }, // TODO: add includeInBilling property
                                    {
                                        label: 'Fecha',
                                        value: (
                                            <DateFormat
                                                value={transaction.date}
                                            />
                                        )
                                    },
                                    { label: 'Descripción', value: transaction.description }
                                ]}
                            />
                        ]}
                    />
                </Grid>
            </Grid>
        </>
    );
};

export default TransactionDetails;
